<template>
  <div class="user-course-reviews">
    <Banner v-if="loading" mode="info">
      <div>
        Loading user course reviews...
      </div>
    </Banner>
    <Banner v-if="loadError" mode="negative">
      <div>
        We encountered an error while loading course reviews. Please try again
        later or file a Feedback issue. Thanks.
      </div>
    </Banner>
    <template v-if="reviews.length">
      <div v-for="review in reviews" :key="review.id">
        <q-card class="q-mb-lg" flat bordered>
          <q-item>
            <q-item-section>
              <q-item-label class="q-my-sm text-h6">{{
                (review.profile && review.profile.displayName) || ""
              }}</q-item-label>
              <q-item-label caption>
                <span class="q-mr-sm">
                  <q-badge
                    color="grey-3"
                    text-color="black"
                    title="Total Reviews"
                  >
                    <q-icon name="stars" class="q-mr-xs"></q-icon
                    >{{ (review.profile && review.profile.ratingsCount) || 0 }}
                  </q-badge>
                </span>
                <span
                  v-if="review.profile && review.profile.handicap"
                  class="q-mr-sm"
                >
                  <q-badge
                    color="grey-3"
                    text-color="black"
                    title="Handicap Range"
                  >
                    <q-icon name="sports_golf" class="q-mr-xs"></q-icon
                    >{{ review.profile.handicap }}
                  </q-badge>
                </span>
                <span
                  v-if="review.profile && review.profile.roundsPerYear"
                  class="q-mr-sm"
                >
                  <q-badge
                    color="grey-3"
                    text-color="black"
                    title="Rounds per year"
                  >
                    <q-icon name="work" class="q-mr-xs"></q-icon
                    >{{ review.profile.roundsPerYear }}
                  </q-badge>
                </span>
              </q-item-label>
            </q-item-section>
          </q-item>
          <q-list>
            <q-expansion-item v-for="(rating, i) in review.ratings" :key="i">
              <template v-slot:header>
                <q-item-section>
                  <span
                    class="text-subtitle2 text-weight-bold"
                    :title="rating.parent.description"
                    >{{ rating.parent.name }}</span
                  >
                </q-item-section>
                <q-item-section side>
                  <q-badge color="grey-3" text-color="black" class="shadow-1">
                    <span class="text-subtitle2">{{
                      rating.parent.rating | numeric
                    }}</span>
                  </q-badge>
                </q-item-section>
              </template>
              <q-card>
                <q-list dense>
                  <q-item v-for="(childRating, i) in rating.children" :key="i">
                    <q-item-section>
                      <span
                        class="text-caption"
                        :title="childRating.description"
                        >{{ childRating.name }}</span
                      >
                    </q-item-section>
                    <q-item-section side>
                      <q-badge color="grey-3" text-color="black">
                        <span class="text-grey-9">{{
                          childRating.rating
                        }}</span>
                      </q-badge>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-card>
            </q-expansion-item>
          </q-list>
          <q-card-section horizontal>
            <q-card-section>
              <div class="text-overline text-grey-7">
                Played on: {{ review.datePlayed | date }}
              </div>
              <div class="review-comments">
                {{ review.comments }}
              </div>
            </q-card-section>
          </q-card-section>
          <!-- TODO: ENABLE THESE EVENTUALLY, should only be visible if has review comments -->
          <!-- <q-card-actions>
          <q-btn-group flat>
            <q-btn size="sm" icon="thumb_up" label="7"></q-btn>
            <q-btn size="sm" icon="thumb_down" label="1"></q-btn>
          </q-btn-group>
        </q-card-actions> -->
        </q-card>
      </div>
    </template>
    <template v-else>
      There are no user reviews for this course yet.
    </template>
  </div>
</template>
<script>
import { getCourseRatingsByCourseId } from "@/apis/course-rating-service";
import Banner from "@/components/Banner";

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
});
const numberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

export default {
  name: "CourseReviews",
  props: {
    courseId: {
      required: true,
      type: String,
    },
  },
  components: {
    Banner,
  },
  data() {
    return {
      loading: false,
      loadError: null,
      reviews: [],
    };
  },
  mounted() {
    this.refreshCourseReviews();
  },
  computed: {},
  filters: {
    date(value) {
      if (!value) {
        return "";
      }

      const date = new Date(value);

      return dateFormatter.format(date);
    },
    numeric(value) {
      return numberFormatter.format(value);
    },
  },
  methods: {
    refreshCourseReviews() {
      const courseId = this.courseId;

      this.loading = true;

      this.fetchCourseReviews(courseId)
        .then(results => {
          console.debug("fetchCourseReviews", results);

          this.reviews = results;
        })
        .catch(error => {
          this.loadError = error;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchCourseReviews(courseId) {
      return getCourseRatingsByCourseId(courseId).then(results => {
        this.courseRatings = results;
        const profiles = results.profiles;
        const meta = [
          {
            id: "course",
            name: "Course",
            courseRatingAttributeId: 1,
          },
          {
            id: "experience",
            name: "Experience",
            courseRatingAttributeId: 5,
          },
        ];
        const reviews = results.reviews.map(review => {
          const profile = profiles.find(p => p.uid === review.uid);
          const courseRatings = review.UserCourseReview_CourseRatings;
          const ratings = meta.map(m => {
            const parent = courseRatings.find(
              cr => cr.courseRatingAttributeId === m.courseRatingAttributeId,
            );
            const courseRatingAttributeCategoryId =
              parent.CourseRatingAttribute.courseRatingAttributeCategoryId;
            const children = courseRatings
              .filter(
                cr =>
                  cr.courseRatingAttributeId !== m.courseRatingAttributeId &&
                  cr.CourseRatingAttribute.courseRatingAttributeCategoryId ===
                    courseRatingAttributeCategoryId,
              )
              .map(cr => {
                const rating = cr.rating;
                const { name, description } = cr.CourseRatingAttribute;

                return {
                  name,
                  description,
                  rating,
                };
              });

            const rating = parent.rating;
            const { name, description } = parent.CourseRatingAttribute;
            const newParent = {
              name,
              description,
              rating,
            };

            return {
              parent: newParent,
              children,
            };
          });

          const newReview = {
            ...review,
            profile,
            ratings,
          };

          return newReview;
        });

        return reviews;
      });
    },
  },
  watch: {},
};
</script>
<style lang="scss" scoped>
.user-course-reviews {
  min-width: 310px;
  max-width: 700px;
}

.review-comments {
  white-space: pre-line;
}
</style>
